import { BaseService } from "@/services/base-service";
import { API_BASE_URL } from "@/config";
import { CancellationReason, isHopCancellable } from "./types";
import { Order } from "@/pages/orders/orders.types";

const ITO_PROXY_PREFIX = "/ito-amznham";
const TERMINAL_STATUSES = ["CANCELLED", "COMPLETED", "DELIVERED"];

export interface CancellationResult {
  success: boolean;
  error?: string;
}

const encodeGreedyPath = (taxonomyId: string): string =>
  taxonomyId.split("/").map(encodeURIComponent).join("/");

class CancellationService extends BaseService {
  private apiBase: BaseService;

  constructor() {
    super(API_BASE_URL + ITO_PROXY_PREFIX);
    this.apiBase = new BaseService(API_BASE_URL);
  }

  public async cancelOrder(orderId: string, reason: CancellationReason): Promise<CancellationResult> {
    try {
      const tokenResponse = await this.apiBase.fetch("/csrf");
      if (!tokenResponse.ok) throw new Error("Failed to get CSRF token");
      const { token: csrfToken } = await tokenResponse.json();

      const encodedOrderId = encodeGreedyPath(orderId);

      const orderResponse = await this.fetch(`/order/${encodedOrderId}`);
      if (!orderResponse.ok) throw new Error("Failed to fetch order");
      const order: Order = await orderResponse.json();

      if (!isHopCancellable(order.items)) {
        return {
          success: false,
          error: "This order can no longer be cancelled because it has progressed in fulfillment. Please refresh the page.",
        };
      }

      const currentDetails = (order as unknown as Record<string, unknown>).details ?? {};
      const statusResponse = await this.fetch(`/order/${encodedOrderId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "x-csrf-token": csrfToken },
        body: JSON.stringify({ status: "CANCELLED", details: { ...currentDetails, cancellationReason: reason } }),
      });
      if (!statusResponse.ok) throw new Error("Failed to update order status");

      const items = order.items ?? [];
      for (const item of items) {
        if (item.status && TERMINAL_STATUSES.includes(item.status.toUpperCase())) continue;

        try {
          await this.fetch(`/orders/${encodeURIComponent(orderId)}/items/${encodeURIComponent(item.itemId)}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json", "x-csrf-token": csrfToken },
            body: JSON.stringify({ status: "CANCELLED" }),
          });
        } catch {
          return {
            success: false,
            error: "Your order was cancelled but some items could not be updated. Please contact support if the issue persists.",
          };
        }
      }

      return { success: true };
    } catch {
      return {
        success: false,
        error: "We couldn't cancel your order. Please try again or contact support.",
      };
    }
  }
}

export const cancellationService = new CancellationService();
